import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ArrowRight, Download } from "lucide-react";

type Priority = "critical" | "high" | "medium" | "low";
type Status = "open" | "in-progress" | "closed";

interface GapItem {
  id: string;
  domain: string;
  currentState: string;
  futureState: string;
  gap: string;
  priority: Priority;
  status: Status;
  owner: string;
  action: string;
}

const PRIORITY_COLORS: Record<Priority, string> = {
  critical: "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/20 dark:text-red-400",
  high: "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/20 dark:text-orange-400",
  medium: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/20 dark:text-amber-400",
  low: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/20 dark:text-green-400",
};

const STATUS_COLORS: Record<Status, string> = {
  open: "bg-muted text-muted-foreground border-border",
  "in-progress": "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/20 dark:text-blue-400",
  closed: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/20 dark:text-green-400",
};

const EMPTY: Omit<GapItem, "id"> = {
  domain: "", currentState: "", futureState: "", gap: "",
  priority: "medium", status: "open", owner: "", action: "",
};

export default function GapAnalysisPanel() {
  const [items, setItems] = useState<GapItem[]>([]);
  const [editing, setEditing] = useState<GapItem | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [formOpen, setFormOpen] = useState(false);
  const [filterDomain, setFilterDomain] = useState("");
  const [filterPriority, setFilterPriority] = useState<Priority | "">("");

  const domains = Array.from(new Set(items.map(i => i.domain).filter(Boolean)));

  const openNew = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };
  const openEdit = (item: GapItem) => { setEditing(item); setForm({ ...item }); setFormOpen(true); };

  const save = () => {
    if (!form.currentState.trim() && !form.gap.trim()) return;
    if (editing) {
      setItems(prev => prev.map(i => i.id === editing.id ? { ...form, id: editing.id } : i));
    } else {
      setItems(prev => [...prev, { ...form, id: crypto.randomUUID() }]);
    }
    setEditing(null);
    setForm(EMPTY);
    setFormOpen(false);
  };

  const del = (id: string) => setItems(prev => prev.filter(i => i.id !== id));

  const filtered = items.filter(i =>
    (!filterDomain || i.domain === filterDomain) &&
    (!filterPriority || i.priority === filterPriority)
  );

  const exportCsv = () => {
    const rows = [
      ["Domain", "Current State (As-Is)", "Future State (To-Be)", "Gap", "Priority", "Status", "Owner", "Action"],
      ...items.map(i => [i.domain, i.currentState, i.futureState, i.gap, i.priority, i.status, i.owner, i.action]),
    ];
    const csv = rows.map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "gap-analysis.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const openCount = items.filter(i => i.status === "open").length;
  const criticalCount = items.filter(i => i.priority === "critical").length;

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 border-b border-border px-5 py-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Gap Analysis — As-Is → To-Be</h2>
          <p className="text-[11px] text-muted-foreground">
            {items.length > 0
              ? `${items.length} gaps · ${openCount} open · ${criticalCount} critical`
              : "Map current state problems to future state improvements."}
          </p>
        </div>
        <div className="flex gap-2">
          {items.length > 0 && <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]" onClick={exportCsv}><Download className="h-3 w-3" /> Export</Button>}
          <Button size="sm" className="h-7 gap-1 text-[11px]" onClick={openNew}><Plus className="h-3 w-3" /> Add Gap</Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" style={{ scrollbarWidth: "thin" }}>

        {/* Form */}
        {formOpen && (
          <div className="border border-border rounded-lg p-4 bg-card space-y-3">
            <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{editing ? "Edit gap" : "New gap"}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Domain / Process Area</label>
                <Input value={form.domain} onChange={e => setForm(f => ({ ...f, domain: e.target.value }))} placeholder="e.g. Patient Registration" className="h-7 text-xs" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Owner</label>
                <Input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} placeholder="Stakeholder responsible" className="h-7 text-xs" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Current State (As-Is)</label>
                <Textarea value={form.currentState} onChange={e => setForm(f => ({ ...f, currentState: e.target.value }))} placeholder="What happens now?" rows={3} className="text-xs" />
              </div>
              <div className="flex items-center justify-center">
                <ArrowRight className="h-5 w-5 text-muted-foreground/40" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Future State (To-Be)</label>
                <Textarea value={form.futureState} onChange={e => setForm(f => ({ ...f, futureState: e.target.value }))} placeholder="What should happen?" rows={3} className="text-xs" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">The Gap</label>
              <Textarea value={form.gap} onChange={e => setForm(f => ({ ...f, gap: e.target.value }))} placeholder="What's missing or broken between current and future state?" rows={2} className="text-xs" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Action to Close Gap</label>
              <Input value={form.action} onChange={e => setForm(f => ({ ...f, action: e.target.value }))} placeholder="What must be built, changed, or removed?" className="h-7 text-xs" />
            </div>
            <div className="flex items-center gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Priority</label>
                <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value as Priority }))}
                  className="h-7 text-xs border border-input rounded-md px-2 bg-background text-foreground">
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Status</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as Status }))}
                  className="h-7 text-xs border border-input rounded-md px-2 bg-background text-foreground">
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div className="flex gap-2 ml-auto mt-4">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(null); setForm(EMPTY); setFormOpen(false); }}>Cancel</Button>
                <Button size="sm" className="h-7 text-xs" onClick={save}>Save</Button>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        {items.length > 1 && (
          <div className="flex gap-2 flex-wrap items-center">
            <select value={filterDomain} onChange={e => setFilterDomain(e.target.value)}
              className="h-7 text-xs border border-input rounded-md px-2 bg-background text-foreground">
              <option value="">All domains</option>
              {domains.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value as Priority | "")}
              className="h-7 text-xs border border-input rounded-md px-2 bg-background text-foreground">
              <option value="">All priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        )}

        {/* Gap cards */}
        {filtered.length === 0 && items.length === 0 && !editing && (
          <div className="text-center py-16 text-muted-foreground">
            <p className="text-sm font-medium">No gaps documented yet</p>
            <p className="text-[11px] mt-1">Start by mapping a current state problem to a future state improvement.</p>
          </div>
        )}

        <div className="space-y-3">
          {filtered.map(item => (
            <div key={item.id} className="border border-border rounded-lg bg-card overflow-hidden cursor-pointer hover:border-muted-foreground/40 transition-colors"
              onClick={() => openEdit(item)}>
              <div className="flex items-center gap-2 px-4 py-2 border-b border-border/50 bg-muted/20">
                {item.domain && <span className="text-[10px] font-mono text-muted-foreground">{item.domain}</span>}
                <div className="flex gap-1.5 ml-auto">
                  <Badge variant="outline" className={`text-[10px] px-1.5 ${PRIORITY_COLORS[item.priority]}`}>{item.priority}</Badge>
                  <Badge variant="outline" className={`text-[10px] px-1.5 ${STATUS_COLORS[item.status]}`}>{item.status}</Badge>
                  {item.owner && <span className="text-[10px] text-muted-foreground">{item.owner}</span>}
                  <button onClick={e => { e.stopPropagation(); del(item.id); }} className="text-muted-foreground hover:text-destructive ml-1">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 divide-x divide-border/50 px-0">
                <div className="px-4 py-3">
                  <p className="text-[9px] font-mono uppercase text-muted-foreground mb-1">As-Is</p>
                  <p className="text-[12px] text-foreground leading-snug">{item.currentState || "—"}</p>
                </div>
                <div className="px-4 py-3">
                  <p className="text-[9px] font-mono uppercase text-muted-foreground mb-1">Gap</p>
                  <p className="text-[12px] text-foreground leading-snug font-medium">{item.gap || "—"}</p>
                </div>
                <div className="px-4 py-3">
                  <p className="text-[9px] font-mono uppercase text-muted-foreground mb-1">To-Be</p>
                  <p className="text-[12px] text-foreground leading-snug">{item.futureState || "—"}</p>
                </div>
              </div>
              {item.action && (
                <div className="px-4 py-2 border-t border-border/50 bg-muted/10">
                  <span className="text-[9px] font-mono uppercase text-muted-foreground">Action: </span>
                  <span className="text-[11px] text-foreground">{item.action}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
